/**
 * Router-compat shim — bridges react-router-dom v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useSearch as tsSearch,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
} from "@tanstack/react-router";
import { useMemo, useCallback, forwardRef, type ComponentProps, type ReactNode } from "react";

// ---------- shared URL parsing ----------

function parseTo(to: string): { pathname: string; search: Record<string, string> | undefined; hash: string | undefined } {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = (beforeHash ?? "").split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr ? Object.fromEntries(new URLSearchParams(searchStr)) : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router.history.go(to);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    if (search) {
      // TanStack strips search params that no route declares via
      // validateSearch, so push the raw URL through history instead — the
      // router subscribes to it and react-router-style "?a=1" targets survive.
      const href = `${pathname === "." ? router.state.location.pathname : pathname}?${new URLSearchParams(search).toString()}${hash ? `#${hash}` : ""}`;
      if (options?.replace) router.history.replace(href, options?.state as never);
      else router.history.push(href, options?.state as never);
      return;
    }
    tsNav({
      to: pathname,
      ...(hash !== undefined ? { hash } : {}),
      state: options?.state as never,
      ...(options?.replace !== undefined ? { replace: options.replace } : {}),
    });
  }, [tsNav, router]) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return tsParams({ strict: false } as never) as T;
}


// ---------- useSearchParams (react-router-dom compat) ----------

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => { searchObj[k] = v; });
      nav({ to: live.pathname, search: searchObj as never, ...(opts?.replace !== undefined ? { replace: opts.replace } : {}) });
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, onClick, target, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  const rawHref = `${pathname}${search ? `?${new URLSearchParams(search).toString()}` : ""}${hash ? `#${hash}` : ""}`;

  // Intern TanStack-navigering normaliserar i vissa migrerade react-router-
  // länkar bort deklarerade queryparametrar trots att href är korrekt. En
  // vanlig ankarlänk bevarar hela URL:en och är därför den säkra vägen för
  // checkout-, auth- och andra länkar vars querydata är obligatorisk.
  if (search) {
    return (
      <a
        ref={ref}
        href={rawHref}
        onClick={onClick}
        {...(target !== undefined ? { target } : {})}
        {...((rest ?? {}) as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children as ReactNode}
      </a>
    );
  }

  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      search={search as never}
      onClick={onClick}
      {...(target !== undefined ? { target } : {})}
      {...(hash !== undefined ? { hash } : {})}
      {...(replace !== undefined ? { replace } : {})}
      state={state as never}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const { pathname, search, hash } = parseTo(to);
  return (
    <TSNavigate
      to={pathname as never}
      search={search as never}
      {...(hash !== undefined ? { hash } : {})}
      state={state as never}
      {...(replace !== undefined ? { replace } : {})}
    />
  );
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

export const NavLink = Link;
